import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Hammer, Sofa, Drill, Paintbrush, ShieldCheck, Upload, X, ArrowRight,
  CheckCircle2, User, Mail, Phone as PhoneIcon, MapPin, MessageSquare, Calendar,
  AlertCircle, Image as ImageIcon, Send, Home, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import WhatToExpect from "@/components/WhatToExpect";

const SERVICES = [
  { id: "House Cleaning (Out-of-Area)", icon: Sparkles,   desc: "Standard or deep cleaning for homes outside our 20-mile Instant Booking radius." },
  { id: "Handyman & Repairs",       icon: Hammer,      desc: "Drywall, doors, hardware, paint touch-ups, weatherstripping." },
  { id: "Furniture Assembly",       icon: Sofa,        desc: "IKEA-style flat-pack, beds, desks, office workstations, outdoor sets." },
  { id: "Fixture Installation",     icon: Drill,       desc: "TVs, mirrors, shelves, blinds, towel bars, ceiling-fan swaps." },
  { id: "Light Home Improvement",   icon: Paintbrush,  desc: "Single-room paint, hardware swaps, backsplash sealing, smart-home install." },
  { id: "Property Management",      icon: ShieldCheck, desc: "Quarterly health-checks, turn-over cleans, seasonal punch lists." },
];

const TIMELINES = [
  { id: "asap",         label: "ASAP",            sub: "This week if possible" },
  { id: "within_week",  label: "Within 1 week" },
  { id: "within_month", label: "Within 1 month" },
  { id: "flexible",     label: "Flexible",         sub: "Whenever works" },
];

const MAX_PHOTOS = 5;
const TARGET_WIDTH = 1400; // resize before upload to keep payload small

// Client-side resize + JPEG encode → base64 data URL
const resizeImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, TARGET_WIDTH / img.width);
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const INITIAL = {
  full_name: "", email: "", phone: "", preferred_contact: "email",
  service_type: "", project_description: "",
  address: "", city: "Evansville", state: "IN", zip_code: "",
  timeline: "flexible",
  photos: [],
};

export default function QuoteRequestPage() {
  useSeo({
    title: "Request a Custom Quote | Handyman, Assembly & More — Top-Notch Pro Solutions",
    description: "Tell us about your project — handyman repairs, furniture assembly, fixture installation, light home improvement, or property management. Photos welcome. We respond within 1 business day.",
  });

  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const fileInput = useRef(null);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (form.photos.length + list.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    const processed = [];
    for (const f of list) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} isn't an image — skipped`); continue; }
      try {
        const dataUrl = await resizeImage(f);
        processed.push(dataUrl);
      } catch {
        toast.error(`Couldn't process ${f.name}`);
      }
    }
    update({ photos: [...form.photos, ...processed].slice(0, MAX_PHOTOS) });
  };

  const removePhoto = (i) => update({ photos: form.photos.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.service_type || !form.project_description) {
      toast.error("Please complete all required fields.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/quote-requests", form);
      setSubmitted(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit your request — please try again or call us.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) return <SuccessPanel request={submitted} onReset={() => { setSubmitted(null); setForm(INITIAL); }} />;

  return (
    <div data-testid="quote-request-page" className="max-w-5xl mx-auto px-6 lg:px-8 py-12 md:py-16">
      <div className="max-w-3xl">
        <span className="section-eyebrow">Custom Quote Request</span>
        <h1 className="heading-display mt-3 text-4xl md:text-5xl">Tell Us About Your Project.</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Need handyman work, assembly, fixtures, light improvements, or ongoing property management?
          <strong> Or cleaning outside the 20-mile instant-booking radius?</strong> Share the details (and a few photos help us quote faster) and we'll get back to you within <strong>1 business day</strong>.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
          <AlertCircle className="w-3.5 h-3.5" />
          Cleaning within 20 miles of Evansville? Use our <Link to="/quote" className="text-[var(--brand-teal)] font-semibold underline">Instant Booking</Link> for an immediate price.
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">

          {/* Service selection */}
          <div className="card-elevated p-6 md:p-8" data-testid="service-card">
            <h2 className="text-lg font-bold text-[var(--brand-teal)] mb-1" style={{ fontFamily: "Outfit" }}>1. Which service?</h2>
            <p className="text-xs text-slate-500 mb-4">Pick the closest match — you can describe specifics below.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => update({ service_type: s.id })}
                  data-testid={`service-${s.id.toLowerCase().replace(/\W+/g, "-")}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
                    form.service_type === s.id
                      ? "border-[var(--brand-teal)] bg-[var(--brand-teal)]/5"
                      : "border-[#dcd5c6] bg-white hover:border-[var(--brand-gold)]"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    form.service_type === s.id ? "bg-[var(--brand-teal)] text-white" : "bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]"
                  }`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-[var(--brand-teal)] text-sm" style={{ fontFamily: "Outfit" }}>{s.id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Project description */}
          <div className="card-elevated p-6 md:p-8" data-testid="description-card">
            <h2 className="text-lg font-bold text-[var(--brand-teal)] mb-4" style={{ fontFamily: "Outfit" }}>2. Describe your project</h2>
            <textarea
              required
              value={form.project_description}
              onChange={(e) => update({ project_description: e.target.value })}
              data-testid="input-description"
              placeholder="The more detail the better — what needs done, materials involved, dimensions, deadline, anything we should know."
              className="form-input min-h-[150px] resize-y"
            />

            {/* Photos */}
            <div className="mt-5">
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-2">Photos (optional, up to {MAX_PHOTOS})</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {form.photos.map((src, i) => (
                  <div key={`photo-${i}-${src.slice(-20)}`} className="relative group aspect-square rounded-lg overflow-hidden border border-[#dcd5c6]" data-testid={`photo-${i}`}>
                    <img src={src} alt={`Project photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {form.photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-[var(--brand-gold)]/60 flex flex-col items-center justify-center text-[var(--brand-gold-deep)] hover:bg-[var(--brand-gold)]/5 transition"
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-[10px] uppercase tracking-wider font-bold">Add Photo</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="photo-file-input"
              />
              <p className="text-[11px] text-slate-500 mt-2">Photos auto-resize on your device for quick upload. Helps us quote accurately on the first try.</p>
            </div>

            <div className="mt-5">
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-2">Timeline</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TIMELINES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => update({ timeline: t.id })}
                    data-testid={`timeline-${t.id}`}
                    className={`p-3 rounded-lg text-left border-2 transition ${
                      form.timeline === t.id ? "border-[var(--brand-teal)] bg-[var(--brand-teal)]/5" : "border-[#dcd5c6] bg-white hover:border-[var(--brand-gold)]"
                    }`}
                  >
                    <div className="font-bold text-[var(--brand-teal)] text-xs" style={{ fontFamily: "Outfit" }}>{t.label}</div>
                    {t.sub && <div className="text-[11px] text-slate-500 mt-0.5">{t.sub}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contact info */}
          <div className="card-elevated p-6 md:p-8" data-testid="contact-card">
            <h2 className="text-lg font-bold text-[var(--brand-teal)] mb-4" style={{ fontFamily: "Outfit" }}>3. Your contact info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field icon={User} label="Full Name *">
                <input required type="text" value={form.full_name} onChange={(e) => update({ full_name: e.target.value })} className="form-input" placeholder="Jane Doe" data-testid="input-name" />
              </Field>
              <Field icon={Mail} label="Email *">
                <input required type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} className="form-input" placeholder="jane@example.com" data-testid="input-email" />
              </Field>
              <Field icon={PhoneIcon} label="Phone *">
                <input required type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} className="form-input" placeholder="(812) 555-0100" data-testid="input-phone" />
              </Field>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-2">Preferred contact</label>
                <div className="flex gap-2">
                  {[
                    { id: "email", label: "Email" },
                    { id: "phone", label: "Call" },
                    { id: "text",  label: "Text" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => update({ preferred_contact: opt.id })}
                      data-testid={`contact-${opt.id}`}
                      className={`flex-1 py-2.5 rounded-lg font-bold text-xs uppercase tracking-wider transition border-2 ${
                        form.preferred_contact === opt.id
                          ? "border-[var(--brand-teal)] bg-[var(--brand-teal)] text-white"
                          : "border-[#dcd5c6] bg-white text-slate-600 hover:border-[var(--brand-teal)]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <Field icon={MapPin} label="Property Address (optional)">
                <input type="text" value={form.address} onChange={(e) => update({ address: e.target.value })} className="form-input" placeholder="123 Main St" data-testid="input-address" />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field icon={Home} label="City">
                  <input type="text" value={form.city} onChange={(e) => update({ city: e.target.value })} className="form-input" data-testid="input-city" />
                </Field>
                <Field icon={MapPin} label="ZIP">
                  <input type="text" value={form.zip_code} onChange={(e) => update({ zip_code: e.target.value })} className="form-input" placeholder="47715" data-testid="input-zip" />
                </Field>
              </div>
            </div>
          </div>
        </div>

        {/* SIDEBAR: Sticky submit + summary */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-4" data-testid="quote-sidebar">
            <div className="card-elevated p-6">
              <h3 className="font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>Your Request</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between"><span className="text-slate-500">Service</span><span className="font-semibold text-[var(--brand-teal)] text-right">{form.service_type || "—"}</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Timeline</span><span className="font-semibold capitalize">{form.timeline.replace("_", " ")}</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Contact</span><span className="font-semibold capitalize">{form.preferred_contact}</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Photos</span><span className="font-semibold">{form.photos.length}/{MAX_PHOTOS}</span></li>
              </ul>
              <button
                type="submit"
                disabled={busy}
                className="btn-accent w-full justify-center mt-5 py-3.5"
                data-testid="submit-quote-request-btn"
              >
                {busy ? "Sending…" : <>Send Request <Send className="w-4 h-4" /></>}
              </button>
              <p className="text-[10px] text-slate-500 text-center mt-3 leading-relaxed" data-testid="legal-disclaimer">
                By submitting, you confirm you are <strong>18+</strong> and agree to our{" "}
                <a href="/terms" className="text-[var(--brand-teal)] underline hover:text-[var(--brand-gold-deep)]" target="_blank" rel="noopener noreferrer">Terms</a>
                {" "}&{" "}
                <a href="/privacy" className="text-[var(--brand-teal)] underline hover:text-[var(--brand-gold-deep)]" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
              <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed">
                We typically reply within 1 business day. For urgent requests call <a href={BRAND.phoneHref} className="text-[var(--brand-teal)] font-semibold">{BRAND.phone}</a>.
              </p>
            </div>

            <div className="rounded-xl bg-[var(--brand-teal)] text-white p-5 text-xs leading-relaxed">
              <div className="flex items-center gap-2 mb-2 text-[var(--brand-gold)] font-bold uppercase tracking-wider text-[10px]">
                <ShieldCheck className="w-3.5 h-3.5" /> Honest Quotes Only
              </div>
              No surprise charges. We give you a clear, written quote before we lift a finger.
            </div>

            <WhatToExpect variant="custom-quote" />
          </div>
        </aside>
      </form>

      <style>{`.form-input { width:100%; padding:12px 14px; border:1.5px solid #dcd5c6; border-radius:10px; background:white; font-family:inherit; color:#0f172a; font-size:14px; transition:border-color 0.2s; }
      .form-input:focus { outline:none; border-color:var(--brand-teal); box-shadow:0 0 0 3px rgba(13,91,94,0.1); }`}</style>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-2">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        {React.cloneElement(children, {
          className: `${children.props.className || ""} form-input`,
          style: { paddingLeft: 38 },
        })}
      </div>
    </div>
  );
}

function SuccessPanel({ request, onReset }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-20" data-testid="quote-request-success">
      <div className="card-elevated p-10 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="heading-display text-3xl md:text-4xl">Request Received!</h1>
        <p className="mt-3 text-slate-600">
          Thanks {request.full_name.split(" ")[0]} — we got your request for <strong className="text-[var(--brand-teal)]">{request.service_type}</strong>.
          We'll reach out to <strong>{request.email}</strong> within 1 business day with a custom quote.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 font-mono bg-[var(--brand-cream-soft)] px-3 py-2 rounded">
          Reference: <span className="text-[var(--brand-teal)] font-bold">{request.id}</span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-outline" data-testid="success-home"><Home className="w-4 h-4" /> Return Home</Link>
          <button onClick={onReset} className="btn-primary" data-testid="success-another">Submit Another Request <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
